// The newsletter set-up form: template, call to action, logo, colours, font,
// footer details, and a live preview of their own choices.
//
// Wave 13 (session 46). Used in two places with the same state shape:
//   * Welcome.jsx, the client's own onboarding ("Your newsletter"), saved
//     through portal_save_onboarding('newsletter', ...)
//   * AdminClientSettings.jsx, where Leena can change any of it (decision 10:
//     there is no client Settings panel in v1), saved as patch.newsletter
//
// The order is Leena's (7a): pick a template from four cards that each show
// the SAME finished Digital Lotus newsletter, then the call to action, then
// logo, colours and font. Footer details only if the business pack lacks them
// (21). After that, one small preview of their template with their own
// choices before they save (7c).
//
// The preview is rendered in the browser from the same slot templates the
// droplet uses (public/newsletter/templates/*.html) through src/lib/newsletter.js,
// the mirror of tools/lotus_newsletter.py. Everything typed here is escaped
// by render(), so nothing a client enters can become markup.
import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import {
  TEMPLATES, FONTS, isHex, loadSample, loadTemplate, normalizeHex, previewSlots, render,
} from '../lib/newsletter.js';

export const DEFAULT_CTA_HEADING = 'Looking for more help?';
export const DEFAULT_CTA_BUTTON = 'Visit our website';
// No SVG: an SVG is a document, not a picture, and the PDF renderer would run
// it. Raster logos only (the validator in the database refuses SVG too).
const LOGO_MIME = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' };
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

export function newsletterFromPack(nl, business) {
  const n = nl || {};
  const website = String(business?.website || '').trim();
  return {
    template: n.template || '',
    headline_font: n.headline_font || '',
    main_color: n.main_color || '#1A7B88',
    accent_color: n.accent_color || '#DC5F13',
    logo_path: n.logo_path || '',
    cta: {
      heading: n.cta?.heading || DEFAULT_CTA_HEADING,
      line: n.cta?.line || '',
      button_text: n.cta?.button_text || DEFAULT_CTA_BUTTON,
      button_url: n.cta?.button_url || website,
    },
    footer: {
      tagline: n.footer?.tagline || business?.tagline || '',
      city: n.footer?.city || business?.city || '',
      phone: n.footer?.phone || business?.phone || '',
      email: n.footer?.email || business?.email || '',
    },
  };
}

// What gets saved: the settings without anything transient (the signed logo
// preview URL lives for five minutes and must never land in the pack).
export function newsletterPayload(v) {
  // Session 64: logo, colours and font live in the brand block now; never
  // write the old copies back from this form.
  const { logo_preview_url, logo_path, main_color, accent_color, headline_font, ...rest } = v || {};
  return rest;
}

export function newsletterComplete(v) {
  return !!(v?.template && (v.cta?.button_text || '').trim() && (v.cta?.button_url || '').trim());
}

/* ------------------------------------------------------- a scaled frame */

function ScaledFrame({ html, scale, width = 600, height, className }) {
  // The email is laid out at 600px wide; the frame scales that down so four
  // of them fit on a page, and the client's own preview stays small (7c).
  return (
    <div className={`nl-frame ${className || ''}`} style={{ width: Math.round(width * scale), height }}>
      <iframe title="Newsletter preview" srcDoc={html} sandbox="" scrolling="no"
              style={{ width, height: Math.round(height / scale), transform: `scale(${scale})`, transformOrigin: '0 0', border: 0, pointerEvents: 'none' }} />
    </div>
  );
}

// Full size in a new tab. The newsletter is wrapped in a sandboxed iframe
// rather than opened as a page of this origin, so even a future raw slot
// could not run script with the portal's session (review of session 46).
function openInTab(html) {
  const attr = html.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  const wrapper = '<!doctype html><title>Newsletter example</title>'
    + '<style>html,body{margin:0;height:100%;background:#F5F8FA}iframe{border:0;width:100%;height:100%}</style>'
    + `<iframe sandbox="" srcdoc="${attr}"></iframe>`;
  const blob = new Blob([wrapper], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/* --------------------------------------------------------------- the form */

// Session 64: logo, colours and font come from the brand block (pack.brand,
// the "Your logo and colours" step / Voice and brand) and are passed in as
// `brand`; this form no longer asks for them. The previews use them.
export default function NewsletterSetup({ value, onChange, business, clientId, brand, askFooter = true, compact = false }) {
  const v = value;
  const pv = brand
    ? { ...v, main_color: brand.main_color, accent_color: brand.accent_color, headline_font: brand.headline_font || '',
        logo_path: brand.logo_path || '', logo_preview_url: brand.logo_preview_url || '' }
    : v;
  const [sample, setSample] = useState(null);
  const [templates, setTemplates] = useState({});
  const [loadError, setLoadError] = useState(null);

  const set = (patch) => onChange({ ...v, ...patch });
  const setCta = (patch) => onChange({ ...v, cta: { ...v.cta, ...patch } });
  const setFooter = (patch) => onChange({ ...v, footer: { ...v.footer, ...patch } });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, ...htmls] = await Promise.all([loadSample(), ...TEMPLATES.map(([t]) => loadTemplate(t))]);
        if (cancelled) return;
        setSample(s);
        setTemplates(Object.fromEntries(TEMPLATES.map(([t], i) => [t, htmls[i]])));
      } catch (e) {
        if (!cancelled) setLoadError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sampleHtml = useMemo(() => {
    if (!sample) return {};
    return Object.fromEntries(TEMPLATES.map(([t]) => [t, templates[t]
      ? render(templates[t], previewSlots(sample, t, pv, business, { useSample: true })) : '']));
  }, [sample, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const previewHtml = useMemo(() => {
    if (!sample || !v.template || !templates[v.template]) return '';
    return render(templates[v.template], previewSlots(sample, v.template, pv, business));
  }, [sample, templates, v, business, brand]);

  return (
    <div className={`nl-setup${compact ? ' nl-compact' : ''}`}>
      {loadError && <div className="banner banner-err">Could not load the newsletter examples: {loadError}</div>}

      {/* ------------------------------------------------ 1. the template */}
      <h3 className="onb-sub">1. Pick a template</h3>
      <p className="hint">
        Each one shows the same finished newsletter, made from one of our own posts,
        so you can see a real one rather than an empty layout. Yours will use your
        logo, colours and words.
      </p>
      <div className="nl-cards" role="radiogroup" aria-label="Newsletter template">
        {TEMPLATES.map(([t, name, why]) => (
          <label key={t} className={`nl-card${v.template === t ? ' nl-card-on' : ''}`}>
            <input type="radio" name="nl-template" value={t} checked={v.template === t}
                   onChange={() => set({ template: t })} />
            {sampleHtml[t]
              ? <ScaledFrame html={sampleHtml[t]} scale={0.38} height={360} className="nl-card-frame" />
              : <div className="nl-frame nl-card-frame nl-frame-empty" style={{ width: 228, height: 360 }}>Loading…</div>}
            <span className="nl-card-text">
              <span className="onb-choice-name">{name}</span>
              <span className="onb-choice-why">{why}</span>
              {sampleHtml[t] && (
                <button type="button" className="link-btn nl-see" onClick={() => openInTab(sampleHtml[t])}>
                  See the whole example
                </button>
              )}
            </span>
          </label>
        ))}
      </div>

      {/* ------------------------------------------- 2. the call to action */}
      <h3 className="onb-sub">2. The call to action</h3>
      <p className="hint">
        Every newsletter ends with this: a heading, an optional line, and a button.
        Where should the button take people? Your booking page, your contact page,
        a special offer.
      </p>
      <div className="inline-fields">
        <label className="field">
          <span className="field-label">Button text</span>
          <input type="text" value={v.cta.button_text} maxLength={80}
                 onChange={(e) => setCta({ button_text: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Where it links</span>
          <input type="text" value={v.cta.button_url} maxLength={500} placeholder="https://…"
                 onChange={(e) => setCta({ button_url: e.target.value })} />
        </label>
      </div>
      <div className="inline-fields">
        <label className="field">
          <span className="field-label">Heading</span>
          <input type="text" value={v.cta.heading} maxLength={80}
                 onChange={(e) => setCta({ heading: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">One line under it (optional)</span>
          <input type="text" value={v.cta.line} maxLength={300}
                 placeholder="A sentence or two about what you can help with."
                 onChange={(e) => setCta({ line: e.target.value })} />
        </label>
      </div>

      {/* Session 64: logo, colours and font are asked once, in the brand
          section, and read from there. */}

      {/* ---------------------------------------------- 4. footer details */}
      {askFooter && (
        <>
          <h3 className="onb-sub">3. The footer</h3>
          <p className="hint">What goes at the bottom of every newsletter, under your business name. All optional.</p>
          <div className="inline-fields">
            <label className="field">
              <span className="field-label">City</span>
              <input type="text" value={v.footer.city} maxLength={200} placeholder="Newbury Park, CA"
                     onChange={(e) => setFooter({ city: e.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">Phone</span>
              <input type="text" value={v.footer.phone} maxLength={200}
                     onChange={(e) => setFooter({ phone: e.target.value })} />
            </label>
          </div>
          <div className="inline-fields">
            <label className="field">
              <span className="field-label">Email people can reply to</span>
              <input type="text" value={v.footer.email} maxLength={200}
                     onChange={(e) => setFooter({ email: e.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">A line about you (optional)</span>
              <input type="text" value={v.footer.tagline} maxLength={200}
                     placeholder="Win your local market with …"
                     onChange={(e) => setFooter({ tagline: e.target.value })} />
            </label>
          </div>
        </>
      )}

      {/* ------------------------------------------------- 5. the preview */}
      <h3 className="onb-sub">{askFooter ? '4.' : '3.'} How yours will look</h3>
      {v.template && previewHtml ? (
        <>
          <p className="hint">
            Your template with your logo, colours and font (from your brand settings) and
            your call to action, on one of our posts. Your own newsletters will be made from your posts.
            {' '}<button type="button" className="link-btn" onClick={() => openInTab(previewHtml)}>Open it full size</button>
          </p>
          <div className="nl-preview">
            <ScaledFrame html={previewHtml} scale={0.5} height={640} />
          </div>
        </>
      ) : (
        <p className="hint">Pick a template above and the preview appears here.</p>
      )}
    </div>
  );
}
